import { useEffect, useRef, useCallback } from 'react';
import { driver, DriveStep, Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import '@/styles/guided-tour.css';
import { useOnboarding } from '@/hooks/useOnboarding';
import { useAuth } from '@/contexts/AuthContext';

const commonSteps: DriveStep[] = [
  {
    element: '[data-tour="sidebar-nav"]',
    popover: {
      title: 'Navigation principale',
      description: 'Accédez rapidement à toutes les sections : Dashboard, Planning, Leads, Dossiers, Clients...',
      side: 'right',
      align: 'start',
    },
  },
  {
    element: '[data-tour="global-search"]',
    popover: {
      title: 'Recherche globale',
      description: 'Trouvez leads, dossiers et clients en tapant ⌘K ou en cliquant ici.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '[data-tour="notifications"]',
    popover: {
      title: 'Centre de notifications',
      description: 'Restez informé des rappels, rendez-vous et actions importantes.',
      side: 'bottom',
      align: 'end',
    },
  },
  {
    element: '[data-tour="dashboard-stats"]',
    popover: {
      title: 'Vos indicateurs clés',
      description: 'Suivez vos leads, dossiers, éligibilités et RDV en temps réel.',
      side: 'bottom',
      align: 'center',
    },
  },
  {
    element: '[data-tour="next-action"]',
    popover: {
      title: 'Prochaine action recommandée',
      description: 'La plateforme vous suggère la meilleure action à effectuer pour optimiser votre journée.',
      side: 'left',
      align: 'start',
    },
  },
];

const onboardingStep: DriveStep = {
  element: '[data-tour="onboarding-checklist"]',
  popover: {
    title: 'Vos premiers pas',
    description: 'Suivez cette checklist pour maîtriser rapidement toutes les fonctionnalités.',
    side: 'right',
    align: 'start',
  },
};

const managerStep: DriveStep = {
  element: '[data-tour="team-toggle"]',
  popover: {
    title: 'Vue équipe',
    description: 'Basculez entre votre vue personnelle et celle de votre équipe pour suivre leurs performances.',
    side: 'bottom',
    align: 'start',
  },
};

interface GuidedTourProps {
  forceStart?: boolean;
  onComplete?: () => void;
}

export function GuidedTour({ forceStart = false, onComplete }: GuidedTourProps) {
  const { user } = useAuth();
  const { onboardingState, isLoading, markTourCompleted, incrementTourViewCount, isOnboardingComplete } = useOnboarding();
  const driverRef = useRef<Driver | null>(null);
  const hasStartedRef = useRef(false);

  const getStepsForUser = useCallback((): DriveStep[] => {
    const steps = [...commonSteps];
    
    // Add onboarding checklist step if not completed
    if (!isOnboardingComplete) {
      steps.push(onboardingStep);
    }
    
    // Add manager-specific step
    if (user?.role === 'manager') {
      steps.splice(4, 0, managerStep); // Insert after dashboard-stats
    }
    
    return steps;
  }, [user?.role, isOnboardingComplete]);

  const startTour = useCallback((isAutoStart = false) => {
    if (driverRef.current) {
      driverRef.current.destroy();
    }

    // Increment view count when auto-starting
    if (isAutoStart) {
      incrementTourViewCount();
    }

    const steps = getStepsForUser();
    
    driverRef.current = driver({
      showProgress: true,
      animate: true,
      overlayOpacity: 0.5,
      stagePadding: 15,
      stageRadius: 8,
      allowClose: true,
      overlayColor: 'rgba(0, 0, 0, 0.5)',
      doneBtnText: 'Terminer',
      nextBtnText: 'Suivant',
      prevBtnText: 'Précédent',
      progressText: '{{current}} sur {{total}}',
      steps,
      onDestroyStarted: () => {
        if (driverRef.current?.hasNextStep()) {
          // User closed before finishing
          driverRef.current.destroy();
        } else {
          // Tour completed
          markTourCompleted();
          onComplete?.();
          driverRef.current?.destroy();
        }
      },
    });

    // Small delay to ensure DOM elements are ready
    setTimeout(() => {
      driverRef.current?.drive();
    }, 300);
  }, [getStepsForUser, markTourCompleted, incrementTourViewCount, onComplete]);

  // Expose startTour globally for the restart button (manual start, not auto)
  useEffect(() => {
    (window as any).__startGuidedTour = () => startTour(false);
    return () => {
      delete (window as any).__startGuidedTour;
    };
  }, [startTour]);

  // Auto-start tour for new users (only first 2 times)
  useEffect(() => {
    const tourViewCount = onboardingState?.tour_view_count ?? 0;
    const shouldAutoStart = forceStart || (
      onboardingState?.has_seen_welcome && 
      tourViewCount < 2
    );

    if (
      !isLoading &&
      !hasStartedRef.current &&
      user &&
      shouldAutoStart
    ) {
      hasStartedRef.current = true;
      // Delay to let the page fully render
      const timer = setTimeout(() => {
        startTour(true); // true = auto-start, increment counter
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [isLoading, onboardingState, forceStart, startTour, user]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (driverRef.current) {
        driverRef.current.destroy();
      }
    };
  }, []);

  // This component doesn't render anything visible
  return null;
}

// Export a function to start the tour programmatically
export function startGuidedTour() {
  if ((window as any).__startGuidedTour) {
    (window as any).__startGuidedTour();
  }
}
